import React from "react";
import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import { IDeliveryBoardItem } from "../../common/interfaces";
import { elapsedText, etaText, FLAGS, KITCHEN, money, REASONS } from "./deliveryFormat";

interface Props {
  delivery: IDeliveryBoardItem;
  selected: boolean;
  onSelect: () => void;
  onAssign: () => void;
  onManualDeliver: () => void;
  onResolve: (action: "retry" | "pool" | "cancel") => void;
  onPhoto: (eventId: number) => void;
}

// Tarjeta de un pedido en el tablero de domicilios.
export const DeliveryCard: React.FC<Props> = ({ delivery: d, selected, onSelect, onAssign, onManualDeliver, onResolve, onPhoto }) => {
  const kitchen = d.kitchen ? KITCHEN[d.kitchen] : null;
  const closed = d.status === "delivered" || d.status === "cancelled";

  return (
    <Paper
      onClick={onSelect}
      sx={{
        p: 1.25,
        mb: 1,
        bgcolor: selected ? "#3a3520" : "#2a2a2a",
        border: selected ? "1px solid #F5E03B" : "1px solid #3a3a3a",
        cursor: "pointer",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 16, flex: 1 }}>#{d.venta_id}</Typography>
        <Typography sx={{ fontSize: 12, color: "#aaa" }}>{elapsedText(d.ordered_at)}</Typography>
      </Box>
      {!!d.customer.name && <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{d.customer.name}</Typography>}
      <Typography sx={{ fontSize: 12, color: "#bbb", lineHeight: 1.35 }}>
        {d.address.text || "Sin dirección"}
        {d.address.lat === null ? " · sin ubicación en mapa" : ""}
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
        {!!d.origin && <Chip size="small" label={d.origin} />}
        {kitchen && !closed && <Chip size="small" label={kitchen.text} sx={{ bgcolor: kitchen.color, color: "#fff" }} />}
        {d.status === "in_route" && d.times.arrived && <Chip size="small" label="Llegó" sx={{ bgcolor: "#F5E03B", color: "#111", fontWeight: 700 }} />}
        {d.cash_due > 0 && <Chip size="small" label={`Cobrar ${money(d.cash_due)}`} sx={{ bgcolor: "#7a4f01", color: "#fff" }} />}
        {d.cash_collected ? <Chip size="small" label={`Cobró ${money(d.cash_collected)}`} sx={{ bgcolor: "#1a3a1a", color: "#8fdc8f" }} /> : null}
        {d.flags.map((f) => <Chip key={f} size="small" label={FLAGS[f] || f} sx={{ bgcolor: "#5c1f1f", color: "#ffb4b4" }} />)}
        {d.sale_cancelled && <Chip size="small" label="Venta cancelada" color="error" />}
      </Box>

      {d.driver && (
        <Typography sx={{ fontSize: 12, mt: 0.75 }}>
          🛵 <b>{d.driver.name}</b>
          {d.status === "in_route" && !d.times.arrived && etaText(d) ? ` · ${etaText(d)}` : ""}
          {d.status === "in_route" && d.times.arrived ? ` · llegó al domicilio hace ${elapsedText(d.times.arrived)}` : ""}
        </Typography>
      )}

      {d.incident && d.status === "failed" && (
        <Box sx={{ mt: 0.75, p: 0.75, bgcolor: "#3a1f1f", borderRadius: 1 }}>
          <Typography sx={{ fontSize: 12, color: "#ffb4b4", fontWeight: 700 }}>
            {REASONS[d.incident.reason || ""] || d.incident.reason} {d.incident.called ? "· llamó al cliente" : ""}
          </Typography>
          {!!d.incident.note && <Typography sx={{ fontSize: 12, color: "#ddd" }}>{d.incident.note}</Typography>}
        </Box>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }} onClick={(e) => e.stopPropagation()}>
        {(d.status === "pending" || d.status === "assigned") && (
          <Button size="small" variant="contained" color="warning" onClick={onAssign}>
            {d.driver ? "Reasignar" : "Asignar"}
          </Button>
        )}
        {d.status === "failed" && (
          <>
            <Button size="small" variant="contained" color="warning" onClick={() => onResolve("retry")}>Reintentar</Button>
            <Button size="small" variant="outlined" onClick={() => onResolve("pool")}>A la bolsa</Button>
            <Button size="small" variant="outlined" color="error" onClick={() => onResolve("cancel")}>Cancelar entrega</Button>
          </>
        )}
        {d.status === "in_route" && (
          <Button size="small" variant="outlined" onClick={onAssign}>Reasignar</Button>
        )}
        {!closed && d.status !== "failed" && (
          <Button size="small" variant="outlined" color="success" onClick={onManualDeliver}>Entregado</Button>
        )}
        {d.incident?.photo_event_id && (
          <Button size="small" variant="text" onClick={() => onPhoto(d.incident!.photo_event_id!)}>Foto incidencia</Button>
        )}
        {d.delivery_photo_event_id && (
          <Button size="small" variant="text" onClick={() => onPhoto(d.delivery_photo_event_id!)}>Evidencia</Button>
        )}
      </Box>
    </Paper>
  );
};
